import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { ArrowLeft, Pencil, UserX, X } from "lucide-react";
import { useState } from "react";
import { toast } from "react-toastify";
import {
	HOUSE_PARTY_LABELS,
	INTEREST_FLAGS,
	SOURCE_LABELS,
	STATUS_LABELS,
	volunteerName,
} from "@/components/volunteers/volunteer-helpers";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useRole } from "@/hooks/use-role";
import { useSession } from "@/hooks/use-session";
import { useUpdateVolunteer, useVolunteer } from "@/hooks/use-volunteers";
import { trackVolunteerFulfillmentUpdated } from "@/lib/analytics-events";
import type {
	HousePartyStatus,
	VolunteerProfile,
	VolunteerProfileUpdate,
} from "@/types/volunteers";

export const Route = createFileRoute("/_app/_/volunteers/$volunteerId")({
	component: VolunteerDetailPage,
});

interface VolunteerForm {
	first_name: string;
	last_name: string;
	email: string;
	phone: string;
	preferred_contact_method: string;
	signup_message: string;
	house_party_status: HousePartyStatus;
	wants_yard_sign: boolean;
	wants_bumper_sticker: boolean;
	wants_to_host_house_party: boolean;
	wants_to_help_raise_money: boolean;
	subscribed_to_updates: boolean;
	yard_sign_delivered: boolean;
	bumper_sticker_delivered: boolean;
}

function toForm(v: VolunteerProfile): VolunteerForm {
	return {
		first_name: v.first_name ?? "",
		last_name: v.last_name ?? "",
		email: v.email ?? "",
		phone: v.phone ?? "",
		preferred_contact_method: v.preferred_contact_method ?? "",
		signup_message: v.signup_message ?? "",
		house_party_status: v.house_party_status,
		wants_yard_sign: v.wants_yard_sign,
		wants_bumper_sticker: v.wants_bumper_sticker,
		wants_to_host_house_party: v.wants_to_host_house_party,
		wants_to_help_raise_money: v.wants_to_help_raise_money,
		subscribed_to_updates: v.subscribed_to_updates,
		yard_sign_delivered: v.yard_sign_delivered,
		bumper_sticker_delivered: v.bumper_sticker_delivered,
	};
}

const ALL_FLAGS = [
	...INTEREST_FLAGS,
	{ key: "yard_sign_delivered", label: "Yard sign delivered" },
	{ key: "bumper_sticker_delivered", label: "Bumper sticker delivered" },
] as const;

function VolunteerDetailPage() {
	const { volunteerId } = Route.useParams();
	const volunteerIdNum = Number(volunteerId);
	const navigate = useNavigate();
	const session = useSession();
	const campaignId = session.campaign?.id ?? 0;
	const { isReadWrite } = useRole();

	const { data: volunteer, isLoading, error } = useVolunteer(
		campaignId,
		volunteerIdNum,
	);
	const updateMutation = useUpdateVolunteer(campaignId);

	const [editing, setEditing] = useState(false);
	const [form, setForm] = useState<VolunteerForm | null>(null);

	if (!campaignId) {
		return (
			<div className="min-h-screen bg-muted/30 p-6">
				<div className="mx-auto max-w-4xl">
					<p className="text-muted-foreground">
						No campaign found. Please complete onboarding first.
					</p>
				</div>
			</div>
		);
	}

	if (isLoading) {
		return (
			<div className="min-h-screen bg-muted/30 p-6">
				<div className="mx-auto flex max-w-4xl items-center justify-center py-20">
					<Spinner className="size-6" />
				</div>
			</div>
		);
	}

	if (error || !volunteer) {
		return (
			<div className="min-h-screen bg-muted/30 p-6">
				<div className="mx-auto max-w-4xl space-y-4">
					<Button
						variant="ghost"
						size="sm"
						onClick={() => navigate({ to: "/volunteers" })}
					>
						<ArrowLeft data-icon="inline-start" className="size-4" />
						Back to volunteers
					</Button>
					<div className="rounded-lg border border-destructive/50 bg-destructive/5 p-6 text-center">
						<p className="text-destructive">
							Failed to load volunteer. It may have been deleted.
						</p>
					</div>
				</div>
			</div>
		);
	}

	const activeForm = form ?? toForm(volunteer);

	function startEdit() {
		setForm(toForm(volunteer!));
		setEditing(true);
	}

	function cancelEdit() {
		setForm(null);
		setEditing(false);
	}

	function handleSave() {
		const f = activeForm;
		const data: VolunteerProfileUpdate = {
			first_name: f.first_name || null,
			last_name: f.last_name || null,
			email: f.email || null,
			phone: f.phone || null,
			preferred_contact_method: f.preferred_contact_method || null,
			signup_message: f.signup_message || null,
			house_party_status: f.house_party_status,
			wants_yard_sign: f.wants_yard_sign,
			wants_bumper_sticker: f.wants_bumper_sticker,
			wants_to_host_house_party: f.wants_to_host_house_party,
			wants_to_help_raise_money: f.wants_to_help_raise_money,
			subscribed_to_updates: f.subscribed_to_updates,
			yard_sign_delivered: f.yard_sign_delivered,
			bumper_sticker_delivered: f.bumper_sticker_delivered,
		};
		updateMutation.mutate(
			{ volunteerId: volunteerIdNum, data },
			{
				onSuccess: () => {
					toast.success("Volunteer updated");
					setEditing(false);
					setForm(null);
				},
				onError: (err) =>
					toast.error(
						err instanceof Error ? err.message : "Failed to update volunteer",
					),
			},
		);
	}

	function handleDeactivate() {
		updateMutation.mutate(
			{ volunteerId: volunteerIdNum, data: { status: "inactive" } },
			{
				onSuccess: () => toast.success("Volunteer deactivated"),
				onError: (err) =>
					toast.error(
						err instanceof Error ? err.message : "Failed to deactivate",
					),
			},
		);
	}

	function patchFlag(key: keyof VolunteerForm, value: boolean) {
		// Outside edit mode the toggles are direct PATCHes (quick fulfillment
		// updates) — mirrors the bulk "mark delivered" action.
		updateMutation.mutate(
			{ volunteerId: volunteerIdNum, data: { [key]: value } },
			{
				onSuccess: () => trackVolunteerFulfillmentUpdated(String(key)),
				onError: (err) =>
					toast.error(
						err instanceof Error ? err.message : "Failed to update",
					),
			},
		);
	}

	const name = volunteerName(volunteer);

	return (
		<div className="min-h-screen bg-muted/30 p-6">
			<div className="mx-auto max-w-4xl space-y-6">
				{/* Header */}
				<div className="flex items-center justify-between gap-3">
					<div className="flex items-center gap-3">
						<Button
							variant="ghost"
							size="icon-sm"
							onClick={() => navigate({ to: "/volunteers" })}
							aria-label="Back to volunteers"
						>
							<ArrowLeft className="size-4" />
						</Button>
						<div>
							<h1 className="text-2xl font-semibold">{name}</h1>
							<div className="mt-1 flex items-center gap-2">
								<Badge
									variant={
										volunteer.status === "active" ? "default" : "secondary"
									}
								>
									{STATUS_LABELS[volunteer.status]}
								</Badge>
								<Badge variant="outline">{SOURCE_LABELS[volunteer.source]}</Badge>
							</div>
						</div>
					</div>

					{isReadWrite && (
						<div className="flex items-center gap-2">
							{editing ? (
								<>
									<Button variant="outline" size="sm" onClick={cancelEdit}>
										<X data-icon="inline-start" className="size-4" />
										Cancel
									</Button>
									<Button
										size="sm"
										onClick={handleSave}
										disabled={updateMutation.isPending}
									>
										Save
									</Button>
								</>
							) : (
								<>
									<Button variant="outline" size="sm" onClick={startEdit}>
										<Pencil data-icon="inline-start" className="size-4" />
										Edit
									</Button>
									{volunteer.status === "active" && (
										<Button
											variant="outline"
											size="sm"
											className="text-destructive hover:text-destructive"
											onClick={handleDeactivate}
											disabled={updateMutation.isPending}
										>
											<UserX data-icon="inline-start" className="size-4" />
											Deactivate
										</Button>
									)}
								</>
							)}
						</div>
					)}
				</div>

				{/* Contact info */}
				<Card>
					<CardHeader>
						<CardTitle className="text-base">Contact information</CardTitle>
					</CardHeader>
					<CardContent className="grid gap-4 sm:grid-cols-2">
						<Field
							label="First name"
							editing={editing}
							value={activeForm.first_name}
							display={volunteer.first_name}
							onChange={(v) =>
								setForm({ ...activeForm, first_name: v })
							}
						/>
						<Field
							label="Last name"
							editing={editing}
							value={activeForm.last_name}
							display={volunteer.last_name}
							onChange={(v) => setForm({ ...activeForm, last_name: v })}
						/>
						<Field
							label="Email"
							editing={editing}
							value={activeForm.email}
							display={volunteer.email}
							onChange={(v) => setForm({ ...activeForm, email: v })}
						/>
						<Field
							label="Phone"
							editing={editing}
							value={activeForm.phone}
							display={volunteer.phone}
							onChange={(v) => setForm({ ...activeForm, phone: v })}
						/>
						<Field
							label="Preferred contact method"
							editing={editing}
							value={activeForm.preferred_contact_method}
							display={volunteer.preferred_contact_method}
							onChange={(v) =>
								setForm({ ...activeForm, preferred_contact_method: v })
							}
						/>
						<div className="space-y-1.5">
							<Label className="text-xs text-muted-foreground">
								Signed up
							</Label>
							<p className="text-sm">
								{volunteer.created_at
									? format(new Date(volunteer.created_at), "MMM d, yyyy 'at' h:mm a")
									: "—"}
							</p>
						</div>
					</CardContent>
				</Card>

				{/* Interests & fulfillment */}
				<Card>
					<CardHeader>
						<CardTitle className="text-base">
							Interests & fulfillment
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-3">
						{ALL_FLAGS.map((flag) => {
							const key = flag.key as keyof VolunteerForm;
							const checked = activeForm[key] as boolean;
							return (
								<div
									key={flag.key}
									className="flex items-center justify-between"
								>
									<span className="text-sm">{flag.label}</span>
									<Switch
										checked={checked}
										disabled={!isReadWrite || updateMutation.isPending}
										onCheckedChange={(c) => {
											if (editing) {
												setForm({ ...activeForm, [key]: c });
											} else {
												patchFlag(key, c);
											}
										}}
										aria-label={flag.label}
									/>
								</div>
							);
						})}

						<Separator />

						<div className="space-y-1.5">
							<Label className="text-xs text-muted-foreground">
								House party status
							</Label>
							{editing ? (
								<Select
									value={activeForm.house_party_status}
									onValueChange={(v) =>
										setForm({
											...activeForm,
											house_party_status: v as HousePartyStatus,
										})
									}
								>
									<SelectTrigger className="h-8 w-48">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{Object.entries(HOUSE_PARTY_LABELS).map(
											([value, label]) => (
												<SelectItem key={value} value={value}>
													{label}
												</SelectItem>
											),
										)}
									</SelectContent>
								</Select>
							) : (
								<div>
									<Badge variant="secondary">
										{HOUSE_PARTY_LABELS[volunteer.house_party_status]}
									</Badge>
								</div>
							)}
						</div>
					</CardContent>
				</Card>

				{/* Signup message */}
				<Card>
					<CardHeader>
						<CardTitle className="text-base">Signup message</CardTitle>
					</CardHeader>
					<CardContent>
						{editing ? (
							<Textarea
								value={activeForm.signup_message}
								onChange={(e) =>
									setForm({ ...activeForm, signup_message: e.target.value })
								}
								placeholder="No message"
								rows={3}
							/>
						) : (
							<p className="text-sm text-muted-foreground">
								{volunteer.signup_message || "No message provided."}
							</p>
						)}
					</CardContent>
				</Card>

				{/* Linked records */}
				<Card>
					<CardHeader>
						<CardTitle className="text-base">Linked records</CardTitle>
					</CardHeader>
					<CardContent className="grid gap-4 sm:grid-cols-2">
						<div className="space-y-1.5">
							<Label className="text-xs text-muted-foreground">Contact</Label>
							{volunteer.contact_id ? (
								<Button
									variant="link"
									className="h-auto p-0 text-sm"
									onClick={() =>
										navigate({
											to: "/contacts/$contactId",
											params: { contactId: String(volunteer.contact_id) },
										})
									}
								>
									View contact #{volunteer.contact_id}
								</Button>
							) : (
								<p className="text-sm text-muted-foreground">
									Not linked to a contact
								</p>
							)}
						</div>
						<div className="space-y-1.5">
							<Label className="text-xs text-muted-foreground">User</Label>
							<p className="text-sm text-muted-foreground">
								{volunteer.user_id
									? `User #${volunteer.user_id}`
									: "No user account"}
							</p>
						</div>
					</CardContent>
				</Card>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Inline-editable text field
// ---------------------------------------------------------------------------

function Field({
	label,
	editing,
	value,
	display,
	onChange,
}: {
	label: string;
	editing: boolean;
	value: string;
	display: string | null;
	onChange: (value: string) => void;
}) {
	return (
		<div className="space-y-1.5">
			<Label className="text-xs text-muted-foreground">{label}</Label>
			{editing ? (
				<Input
					value={value}
					onChange={(e) => onChange(e.target.value)}
					className="h-9"
				/>
			) : (
				<p className="text-sm">{display || "—"}</p>
			)}
		</div>
	);
}
